import { uniq } from 'Utility/ramdaReplacement';

import type { TMS } from 'neuro-schemas/diagnosis';
import type { IMSA2022 } from 'neuro-schemas/msa';

type TMsTypes = TMS;

type TPDDiagnosis = 'G20';
type TPSPDiagnosis = 'G23.1';
type TMSADiagnosis = 'G23.2' | 'G23.3' | 'G90.3';
type TCBDDiagnosis = 'G25.9' | 'G31.0';
type TDLBDiagnosis = 'F02.8*G31.8' | 'F00.2*G30.8';
type TFTDDiagnosis = 'F02.0*G31.0';
type TOtherExtraPyramidalDisordersDiagnosis =
  | 'G25'
  | 'G25.0'
  | 'G25.1#'
  | 'G25.2'
  | 'G25.3#'
  | 'G25.4#'
  | 'G25.5'
  | 'G25.6#'
  | 'G25.8';
export type TAlzheimerDiagnosis = 'G30' | 'G30.0' | 'G30.1' | 'G30.8' | 'G30.9' | 'R41.8' | 'alzheimerSuspicion';

export const smaDiagnoses: Array<TSMADiagnosis> = ['G12.0', 'G12.1'];
export const dmdDiagnoses: Array<TDMDDiagnosis> = ['G71.06'];

export const pdDiagnoses: Array<TPDDiagnosis> = ['G20'];
export const pspDiagnoses: Array<TPSPDiagnosis> = ['G23.1'];
export const msaDiagnoses: Array<TMSADiagnosis> = ['G23.2', 'G23.3', 'G90.3'];
export const cbdDiagnoses: Array<TCBDDiagnosis> = ['G25.9', 'G31.0'];
export const dlbDiagnoses: Array<TDLBDiagnosis> = ['F00.2*G30.8', 'F02.8*G31.8'];
const ftdDiagnoses: Array<TFTDDiagnosis> = ['F02.0*G31.0'];
const otherExtraPyramidalDisorders: Array<TOtherExtraPyramidalDisordersDiagnosis> = [
  'G25',
  'G25.0',
  'G25.1#',
  'G25.2',
  'G25.3#',
  'G25.4#',
  'G25.5',
  'G25.6#',
  'G25.8',
];
export const alzheimerDiagnoses: Array<TAlzheimerDiagnosis> = [
  'G30',
  'G30.0',
  'G30.1',
  'G30.8',
  'G30.9',
  'R41.8',
  'alzheimerSuspicion',
];

export const parkinsonDiagnoses: Array<TParkinsonDiagnosis> = [
  ...pdDiagnoses,
  ...pspDiagnoses,
  ...msaDiagnoses,
  ...cbdDiagnoses,
  ...dlbDiagnoses,
  ...ftdDiagnoses,
  ...otherExtraPyramidalDisorders,
  ...alzheimerDiagnoses,
];
export const msDiagnoses: Array<TMSDiagnosis> = ['G35'];
export const keoDiagnoses: Array<TMSDiagnosis> = ['G36.9', 'G37.9', 'G36.0'];
export const reoDiagnoses: Array<TMSDiagnosis> = ['radiologicallyIsolatedSyndrome'];
export const nmosdDiagnoses: Array<TMSDiagnosis> = ['G36.0'];
export const huntingtonDiagnoses: Array<THuntingtonDiagnosis> = ['G10', 'Z82'];
export const epilepsyDiagnoses: Array<TEpilepsyDiagnosis> = [
  'G40',
  'G40.0',
  'G40.00',
  'G40.01',
  'G40.09',
  'G40.1',
  'G40.10',
  'G40.11',
  'G40.12',
  'G40.19',
  'G40.2',
  'G40.20',
  'G40.21',
  'G40.22',
  'G40.29',
  'G40.3',
  'G40.30',
  'G40.31',
  'G40.33',
  'G40.34',
  'G40.35',
  'G40.36',
  'G40.37',
  'G40.39',
  'G40.4',
  'G40.5',
  'G40.50',
  'G40.51',
  'G40.52#',
  'G40.59',
  'G40.6',
  'G40.7',
  'G40.80',
  'G40.89',
  'G40.9',
  'G41',
  'G41.0',
  'G41.1',
  'G41.2',
  'G41.8',
  'G41.9',
];
export const sleepApneaDiagnoses: Array<TSleepApneaDiagnosis> = [
  'G47.3',
  'J96.1',
  'J96.9',
  'sleepApneaSuspicion',
  'respiratoryFailureSuspicion',
];
export const mgravisDiagnoses: Array<TMgravisDiagnosis> = ['G70.0', 'G73.1'];

export const hereditaryDGs: Array<TPolyneuroDiagnosis> = [
  'G60.0',
  'G60.1',
  'G60.2',
  'G60.3',
  'G60.8',
  'G60.9',
  'E85.1',
  'E85.8',
];

export const inflammatoryDGs: Array<TPolyneuroDiagnosis> = ['G61.0', 'G61.1', 'G61.8', 'G61.9'];
export const monoclonalDGs: Array<TPolyneuroDiagnosis> = ['D47.2'];

export const polyneuroDiagnoses: Array<TPolyneuroDiagnosis> = [...hereditaryDGs, ...inflammatoryDGs, ...monoclonalDGs];

/** All diagnoses of all platforms, including _unspecified diagnosis_  */
export const allDiagnoses: Array<TDiagnosis> = uniq([
  ...smaDiagnoses,
  ...dmdDiagnoses,
  ...parkinsonDiagnoses,
  ...msDiagnoses,
  ...keoDiagnoses,
  ...reoDiagnoses,
  ...nmosdDiagnoses,
  ...huntingtonDiagnoses,
  ...epilepsyDiagnoses,
  ...sleepApneaDiagnoses,
  ...mgravisDiagnoses,
  ...polyneuroDiagnoses,
  'unspecifiedDiagnosis',
]);
export const msTypes: Array<TMsTypes> = ['RR', 'SP', 'PP', 'cantDefine', 'unknown'];

export const coreClinicalFields = [
  'fluctuatingCognitionWithPronouncedVariationsInAttentionAndAlertness',
  'recurrentVisualHallucinationsThatAreTypicallyWellFormedAndDetailed',
  'remSleepBehaviourDisorderWhichMayPrecedeCognitiveDecline',
  'oneOrMoreSpontaneousCardinalFeaturesOfParkinsonism',
];

export const supportiveClinicalFields = [
  'severeSensitivityToAntipsycoticAgents',
  'posturalInstability',
  'repeatedFalls',
  'syncopeOrOtherTransientEpisodesOfUnresponsiveness',
  'severeAutonomicDysfunction',
  'hypersomnia',
  'hyposmia',
  'hallucinationsInOtherModalities',
  'systematizedDelusions',
  'apathyAnxietyAndDepression',
];

export const indicativeBiomarkerFields = [
  'reducedDopamineTransporterUptakeInBasalGangliaDemonstratedBySPECTorPET',
  'abnormalIodineMIBGMyocardialScintigraphy',
  'polysomnographicConfirmationOfREMsleepWithoutAtonia',
];

export const supportiveBiomarkerFields = [
  'relativePreservationOfMedicalTemporalLobeStructuresOnCTMRIScan',
  'generalizedLowUptakeOnSPECTPETperfusionMetabolismScanWithReducedOccipitalActivity',
  'prominentPosteriorSlowWaveActivityOnEEGwithPeriodicFluctuationsInThePreAlphaThetaRange',
];

export interface IDiagnosisPairs {
  header: React.JSX.Element | string;
  value: React.JSX.Element | string | number;
  date?: PartialDate | undefined | null;
  key?: string;
}

export type TParkinsonCriteriaType = IPD | IMSA | IMSA2022 | IPSP | IDLB | ICBD | IFTD | IPPA | IAlzheimer;

export const parkinsonCriteriaTypes = ['pd', 'msa', 'dlb', 'psp', 'cbd', 'ftd', 'ppa', 'alzheimer'] as const;

export interface IDescription {
  fields: Array<string>;
  section: string;
  marginLeft: number;
}

export interface ISection {
  descriptions: Array<IDescription>;
  section: string;
  marginTop: number;
}

export const descriptionSectionArrayPD = [
  {
    descriptions: [
      {
        fields: ['1', '2', '3'],
        section: 'description.clinicallyEstablished',
        marginLeft: 2,
      },
    ] as Array<IDescription>,
    section: 'clinicallyEstablished',
    marginTop: 1,
  },
  {
    descriptions: [
      {
        fields: ['1', '2'],
        section: 'description.clinicallyProbable',
        marginLeft: 2,
      },
      {
        fields: ['if1RedFlag', 'if2RedFlags', 'noMoreThan2RedFlags'],
        section: 'description.clinicallyProbable',
        marginLeft: 3.7,
      },
    ] as Array<IDescription>,
    section: 'clinicallyProbable',
    marginTop: 1,
  },
] as Array<ISection>;

export const dxCategories: ICategoryOptions[] = [
  {
    title: 'diagnosis.opts.SMA',
    localization: 'diagnosis.sma',
    values: smaDiagnoses,
    key: 'sma',
  },
  {
    title: 'diagnosis.opts.DMD',
    localization: 'diagnosis.dmd',
    values: dmdDiagnoses,
    key: 'dmd',
  },
  {
    title: 'diagnosis.opts.PD',
    localization: 'diagnosis.parkinson',
    values: pdDiagnoses,
    key: 'parkinson',
  },
  {
    title: 'diagnosis.opts.otherExtraPyramidalDisorders',
    localization: 'diagnosis.otherExtraPyramidalDisorders',
    values: otherExtraPyramidalDisorders,
    key: 'parkinson',
  },
  {
    title: 'diagnosis.opts.PSP',
    localization: 'diagnosis.psp.title',
    values: pspDiagnoses,
    key: 'parkinson',
  },
  {
    title: 'diagnosis.opts.MSA',
    localization: 'diagnosis.msa.title',
    values: msaDiagnoses,
    key: 'parkinson',
  },
  {
    title: 'diagnosis.opts.CBD',
    localization: 'diagnosis.cbd.title',
    values: cbdDiagnoses,
    key: 'parkinson',
  },
  {
    title: 'diagnosis.opts.DLB',
    localization: 'diagnosis.dlb.title',
    values: dlbDiagnoses,
    key: 'parkinson',
  },
  {
    title: 'diagnosis.opts.FTD',
    localization: 'diagnosis.ftd.titleLong',
    values: ftdDiagnoses,
    key: 'parkinson',
  },
  {
    title: 'diagnosis.opts.alzheimer',
    localization: 'diagnosis.alzheimer.title',
    values: alzheimerDiagnoses,
    key: 'parkinson',
  },
  {
    title: 'diagnosis.opts.MS',
    localization: 'diagnosis.ms',
    values: msDiagnoses,
    key: 'ms',
  },
  {
    title: 'diagnosis.opts.KEO',
    localization: 'diagnosis.keo',
    values: keoDiagnoses,
    key: 'ms',
  },
  {
    title: 'diagnosis.opts.REO',
    localization: 'diagnosis.reo',
    values: reoDiagnoses,
    key: 'ms',
  },
  {
    title: 'diagnosis.opts.NMOSD',
    localization: 'diagnosis.nmosd',
    values: nmosdDiagnoses,
    key: 'ms',
  },
  {
    title: 'diagnosis.opts.HD',
    localization: 'diagnosis.huntington.title',
    values: huntingtonDiagnoses,
    key: 'huntington',
  },
  {
    title: 'diagnosis.opts.EP',
    localization: 'diagnosis.epilepsy.title',
    values: epilepsyDiagnoses,
    key: 'epilepsy',
  },
  {
    title: 'diagnosis.opts.SR',
    localization: 'diagnosis.sleepApnea.title',
    values: sleepApneaDiagnoses,
    key: 'sleepApnea',
  },
  {
    title: 'diagnosis.opts.mgravis',
    localization: 'diagnosis.mgravis.title',
    values: mgravisDiagnoses,
    key: 'mgravis',
  },
  {
    title: 'diagnosis.opts.polyneuro.hereditary',
    localization: 'diagnosis.opts.polyneuro.hereditary',
    values: hereditaryDGs,
    key: 'polyneuro',
  },
  {
    title: 'diagnosis.opts.polyneuro.inflammatory',
    localization: 'diagnosis.opts.polyneuro.inflammatory',
    values: inflammatoryDGs,
    key: 'polyneuro',
  },
  {
    title: 'diagnosis.opts.polyneuro.monoclonal',
    localization: 'diagnosis.opts.polyneuro.monoclonal',
    values: monoclonalDGs,
    key: 'polyneuro',
  },
  // {
  //   title: 'diagnosis.opts.unspecified',
  //   localization: 'diagnosis.opts.unspecified',
  //   values: allDiagnoses.filter(dx => dx.includes('unspecified')),
  //   key: 'unspecified',
  // },
];

export const epilepsySyndromeOptionsWithHeaders: IRadio['options'] = [
  {
    type: 'header',
    level: 0,
    text: 'header1.',
    style: { fontSize: '1.8rem' },
  },
  {
    type: 'header',
    level: 1,
    text: 'header1.1.',
  },
  {
    type: 'option',
    level: 2,
    value: 'geneticEpilepsyWithFebrileConvulsionsAndOtherSeizures', // 1.1.1.
  },
  {
    type: 'option',
    level: 2,
    value: 'infantileSelfLimitingEpilepsy', // 1.1.2.
  },
  {
    type: 'option',
    level: 2,
    value: 'infantileMyoclonicEpilepsy', // 1.1.3.
  },
  {
    type: 'option',
    level: 2,
    value: 'neonatalSelfLimitingFamilialEpilepsy', // 1.1.4.
  },
  {
    type: 'option',
    level: 2,
    value: 'neonatalSelfLimitingNonFamilialEpilepsy', // 1.1.5.
  },
  {
    type: 'header',
    level: 1,
    text: 'header1.2.',
  },
  {
    type: 'option',
    level: 2,
    value: 'dravetSyndrome', // 1.2.1.
  },
  {
    type: 'option',
    level: 2,
    value: 'infantileEpilepsyWithMigratorySeizuresOfLocalOrigin', // 1.2.2.
  },
  {
    type: 'option',
    level: 2,
    value: 'infantileSpasmSyndrome', // 1.2.3.
  },
  {
    type: 'option',
    level: 2,
    value: 'earlyOnsetInfantileEpilepticEncephalopathy', // 1.2.4.
  },
  {
    type: 'header',
    level: 1,
    text: 'header1.3.',
  },
  {
    type: 'option',
    level: 2,
    value: 'CDKL5DevelopmentalAndEpilepticEncephalopathy', // 1.3.1.
  },
  {
    type: 'option',
    level: 2,
    value: 'glucoseTransporter1DeficiencySyndrome', // 1.3.2.
  },
  {
    type: 'option',
    level: 2,
    value: 'gelasticSeizuresWithHypothalamicHamartoma', // 1.3.3.
  },
  {
    type: 'option',
    level: 2,
    value: 'KCNQ2DevelopmentalAndEpilepticEncephalopathy', // 1.3.4.
  },
  {
    type: 'option',
    level: 2,
    value: 'PCDH19ClusteringEpilepsy', // 1.3.5.
  },
  {
    type: 'option',
    level: 2,
    value: 'pyridoxineDependentDevelopmentalAndEpilepticEncephalopathy', // 1.3.6.
  },
  {
    type: 'option',
    level: 2,
    value: 'pyridoxamine5PhosphateDeficiencyDevelopmentalAndEpilepticEncephalopathy', // 1.3.7.
  },
  {
    type: 'option',
    level: 2,
    value: 'sturgeWeberSyndrome', // 1.3.8.
  },
  {
    type: 'header',
    level: 0,
    text: 'header2.',
    style: { fontSize: '1.8rem', marginTop: '3rem' },
  },
  {
    type: 'header',
    level: 1,
    text: 'header2.1.',
  },
  {
    type: 'option',
    level: 2,
    value: 'selfLimitingEpilepsyWithCentrotemporalSpikes', // 2.1.1.
  },
  {
    type: 'option',
    level: 2,
    value: 'selfLimitingEpilepsyWithAutonomicSeizures', // 2.1.2.
  },
  {
    type: 'option',
    level: 2,
    value: 'childhoodOccipitalLobeEpilepsyWithVisualSymptoms', // 2.1.3.
  },
  {
    type: 'option',
    level: 2,
    value: 'fotosensitiveOccipitalLobeEpilepsy', // 2.1.4.
  },
  {
    type: 'header',
    level: 1,
    text: 'header2.2.',
  },
  {
    type: 'option',
    level: 2,
    value: 'epilepsyWithMyoclonicAbsenceSeizures', // 2.2.1.
  },
  {
    type: 'option',
    level: 2,
    value: 'epilepsyWithEyelidMyoclonus', // 2.2.2.
  },
  {
    type: 'header',
    level: 1,
    text: 'header2.3.',
  },
  {
    type: 'option',
    level: 2,
    value: 'epilepticEncephalopathyWithSpikeAndWaveActivationInSleep', // 2.3.1.
  },
  {
    type: 'option',
    level: 2,
    value: 'developmentalAndEpilepticEncephalopathyWithSleepDischargesActivatingDuringEEG', // 2.3.2.
  },
  {
    type: 'option',
    level: 2,
    value: 'epilepticSyndromeAssociatedWithFebrileInfection', // 2.3.3.
  },
  {
    type: 'option',
    level: 2,
    value: 'lennoxGastautSyndrome', // 2.3.4.
  },
  {
    type: 'option',
    level: 2,
    value: 'myoclonicAtonicEpilepsy', // 2.3.5.
  },
  {
    type: 'option',
    level: 2,
    value: 'hemiconvulsionHemiplegiaEpilepsy', // 2.3.6.
  },
  {
    type: 'header',
    level: 0,
    text: 'header3.',
    style: { fontSize: '1.8rem', marginTop: '3rem' },
  },
  {
    type: 'option',
    level: 1,
    value: 'hearingRelatedEpilepsy', // 3.1.
  },
  {
    type: 'option',
    level: 1,
    value: 'readingInducedSeizuresEpilepsy', // 3.2.
  },
  {
    type: 'option',
    level: 1,
    value: 'familialFocalEpilepsyWithVariableFoci', // 3.3.
  },
  {
    type: 'option',
    level: 1,
    value: 'mesialTemporalLobeEpilepsyWithHippocampalSclerosis', // 3.4.
  },
  {
    type: 'option',
    level: 1,
    value: 'progressiveMyoclonicEpilepsy', // 3.5.
  },
  {
    type: 'option',
    level: 1,
    value: 'rasmussenSyndrome', // 3.6.
  },
  {
    type: 'option',
    level: 1,
    value: 'familialMesialTemporalLobeEpilepsy', // 3.7.
  },
  {
    type: 'option',
    level: 1,
    value: 'sleepRelatedHypermotoricEpilepsy', // 3.8.
  },
  {
    type: 'header',
    level: 0,
    text: 'header4.',
    style: { fontSize: '1.8rem', marginTop: '3rem' },
  },
  {
    type: 'option',
    level: 1,
    value: 'childhoodAbsenceEpilepsy', // 4.1.
  },
  {
    type: 'option',
    level: 1,
    value: 'youthMyoclonicEpilepsy', // 4.2.
  },
  {
    type: 'option',
    level: 1,
    value: 'youthAbsenceEpilepsy', // 4.3.
  },
  {
    type: 'option',
    level: 1,
    value: 'generalizedTonicClonicSeizuresAloneEpilepsy', // 4.4.
  },
  {
    type: 'header',
    level: 0,
    text: 'header5.',
    style: { fontSize: '1.8rem', marginTop: '3rem' },
  },
  {
    type: 'option',
    level: 1,
    value: 'epilepsyTypeOrSyndromeUnknown', // 5.1.
  },
  {
    type: 'header',
    level: 0,
    text: 'header6.',
    style: { fontSize: '1.8rem', marginTop: '3rem' },
  },
  {
    type: 'option',
    level: 1,
    value: 'temporalLobeEpilepsy', // 6.1.
  },
  {
    type: 'option',
    level: 1,
    value: 'frontalLobeEpilepsy', // 6.2.
  },
  {
    type: 'option',
    level: 1,
    value: 'parietalLobeEpilepsy', // 6.3.
  },
  {
    type: 'option',
    level: 1,
    value: 'occipitalLobeEpilepsy', // 6.4.
  },
  {
    type: 'option',
    level: 1,
    value: 'localUnspecifiedEpilepsy', // 6.5.
  },
  {
    type: 'option',
    level: 1,
    value: 'generalUnspecifiedEpilepsy', // 6.6
  },
  {
    type: 'option',
    level: 1,
    value: 'otherKnownSyndrome', // 6.7
  },
];
